/* eslint-disable @typescript-eslint/no-explicit-any */

import { GetBatteryListParams } from '../../endpoints/seller/SellerBatteryEndpoints';
import apiClient from '../../apiClient';
import API_ENDPOINTS from '../../endpoints/apiEndpoints';
import { BaseResponse } from '../UtilsInterface';
import {
  BatteriesManufacturersData,
  BatteryApiResponse,
  BatteryDamageInfoData,
  BatteryImageApiResponse,
  BatteryInfoData,
  BatteryMeasurementsApiResponse,
  BatteryOverviewData,
  BatterySellerData,
  DeleteBatteryBody,
  ManufacturerModelsData,
  UpdateBatteryDamageInfoBody,
} from '../BatteryInterfaces';

const SellerBatteryService = {
  // 배터리 보고서 조회
  getBatteryDamageInfo: async (
    batteryId: string
  ): Promise<BaseResponse<BatteryDamageInfoData>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_damage_info(batteryId)
      );
      return response.data;
    } catch (error) {
      console.error('배터리 보고서 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 정보 수정
  updateBatteryDamageInfo: async (
    batteryId: string,
    data: UpdateBatteryDamageInfoBody
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.put_battery_seller_damage_info_update(batteryId),
        data
      );
      return response.data;
    } catch (error) {
      console.error('배터리 정보 수정 실패:', error);
      throw error;
    }
  },

  // 배터리 삭제
  deleteBattery: async (
    data: DeleteBatteryBody
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.request({
        method: 'DELETE', // DELETE 메서드 지정
        url: API_ENDPOINTS.delete_battery(), // API 엔드포인트
        data, // body에 데이터 포함
      });
      return response.data; // 응답 데이터 반환
    } catch (error) {
      console.error('배터리 삭제 실패:', error);
      throw error; // 에러 다시 던지기
    }
  },

  // 배터리 판매 상태 변경
  updateBatterySaleStatus: async (
    batteryId: string,
    saleStatus: string
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.put_battery_seller_sale_status(batteryId),
        { saleStatus } // JSON 객체로 감싸기
      );
      return response.data;
    } catch (error) {
      console.error('배터리 판매 상태 변경 실패:', error);
      throw error;
    }
  },

  // 대표 이미지 설정
  updateRepresentativeImage: async (
    batteryId: string,
    batteryImageId: string
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.put_battery_seller_representative_image(batteryId),
        { batteryImageId }
      );
      return response.data;
    } catch (error) {
      console.error('대표 이미지 설정 실패:', error);
      throw error;
    }
  },

  // 배터리 QR 코드 등록
  registerBatteryQrCode: async (
    batteryId: string,
    qrData: any
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.put_battery_seller_qrcode(batteryId),
        qrData
      );
      return response.data;
    } catch (error) {
      console.error('QR 코드 등록 실패:', error);
      throw error;
    }
  },

  // 배터리 리스트 조회
  getBatteryList: async (
    params: GetBatteryListParams
  ): Promise<BatteryApiResponse> => {
    try {
      const url = API_ENDPOINTS.get_battery_seller_list(params); // 동적으로 URL 생성
      const response = await apiClient.get(url); // 생성된 URL로 요청
      return response.data;
    } catch (error) {
      console.error('배터리 리스트 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 등록
  registerBattery: async (batteryData: any): Promise<BaseResponse<any>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_battery_seller_register(),
        batteryData
      );
      return response.data;
    } catch (error) {
      console.error('배터리 등록 실패:', error);
      throw error;
    }
  },

  // 배터리 측정 데이터 조회
  getBatteryMeasurements: async (
    batteryId: string,
    page: number,
    size: number,
    category: string
  ): Promise<BatteryMeasurementsApiResponse> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_measurements(
          batteryId,
          page,
          size,
          category
        )
      );
      return response.data;
    } catch (error) {
      console.error('배터리 측정 데이터 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 측정 데이터 업로드
  uploadBatteryMeasurements: async (
    batteryId: string,
    measurements: any
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_battery_seller_measurements(batteryId),
        measurements
      );
      return response.data;
    } catch (error) {
      console.error('배터리 측정 데이터 업로드 실패:', error);
      throw error;
    }
  },

  // 배터리 이미지 조회
  getBatteryImages: async (
    batteryId: string,
    page: number,
    size: number
  ): Promise<BatteryImageApiResponse> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_images(batteryId, page, size)
      );
      return response.data;
    } catch (error) {
      console.error('배터리 이미지 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 이미지 업로드
  uploadBatteryImages: async (
    batteryId: string,
    imageData: any
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_battery_seller_images(batteryId),
        imageData
      );
      return response.data;
    } catch (error) {
      console.error('배터리 이미지 업로드 실패:', error);
      throw error;
    }
  },

  // 판매자 정보 조회
  getBatterySellers: async (
    batteryId: string
  ): Promise<BaseResponse<BatterySellerData>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_sellers(batteryId)
      );
      return response.data;
    } catch (error) {
      console.error('판매자 정보 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 진단 보고서
  getBatteryOverview: async (
    batteryId: string
  ): Promise<BaseResponse<BatteryOverviewData[]>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_overview(batteryId)
      );
      return response.data;
    } catch (error) {
      console.error('배터리 요약 정보 조회 실패:', error);
      throw error;
    }
  },

  // 배터리 진단 보고서
  getBatteryInfo: async (
    batteryId: string
  ): Promise<BaseResponse<BatteryInfoData>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_info(batteryId)
      );
      return response.data;
    } catch (error) {
      console.error('배터리 요약 정보 조회 실패:', error);
      throw error;
    }
  },

  // 제조사 목록 조회
  getBatteryManufacturers: async (): Promise<
    BaseResponse<BatteriesManufacturersData[]>
  > => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_manufacturers()
      );
      return response.data;
    } catch (error) {
      console.error('제조사 목록 조회 실패:', error);
      throw error;
    }
  },

  // 제조사 목록 조회
  getManufacturersModel: async (
    carManufacturerId: string
  ): Promise<BaseResponse<ManufacturerModelsData[]>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_battery_seller_manufacturer_models(carManufacturerId)
      );
      return response.data;
    } catch (error) {
      console.error('제조사 목록 조회 실패:', error);
      throw error;
    }
  },
};

export default SellerBatteryService;
